import * as React from 'react'
import {
  dispatchToProps,
  formName,
  IRecordingsListContainerProps,
} from './RecordingsList.container'
import { moment } from 'features/Moment'
import { Table, TableHeader, TableRow } from 'features/Table'
import Pagination from 'components/Pagination'
import EmptyMessageCard from 'components/Cards/EmptyMessageCard'
import Button from 'components/Button'
import { Row, Col } from 'components/Layout'
import cn from 'classnames'
import * as sLay from 'styles/modules/layout.scss'
import * as sMarg from 'styles/modules/margins.scss'
import * as sText from 'components/Text/Text.scss'
import LoadingOverlay from 'components/Loading/LoadingOverlay'
import LoadingDots from 'components/Loading/LoadingDots'
import config from 'src/config'
import SelectPretty from 'components/Input/SelectPretty'
import { change, Field } from 'redux-form'
import Autosuggest from 'features/Autosuggest'
import { useListRecordingsQuery } from 'src/generated/graphql'
import useMount from 'react-use/lib/useMount'

export interface IRecordingsListComponentProps extends IRecordingsListContainerProps {
  numberOptions: any
  banOptions: any
  page: number
  numbersById: object
  getNumbers: typeof dispatchToProps['getNumbers']
  replace: typeof dispatchToProps['replace']
  dispatch: typeof dispatchToProps.dispatch
}

export const RecordingsListComponent = (props: IRecordingsListComponentProps) => {
  const {
    page,
    replace,
    numbersById,
    banOptions,
    numberOptions,
    serviceId,
    ban,
    from,
    getNumbers,
    dispatch,
  } = props

  useMount(() => {
    if (!numbersById || Object.keys(numbersById).length === 0) {
      getNumbers()
    }
  })

  const selectNumber = serviceId => {
    replace({
      pathname: '/dashboard/recordings',
      query: { serviceId, ban, page: 1, from },
    })
  }

  const selectBan = ban => {
    replace({
      pathname: '/dashboard/recordings',
      query: { ban, serviceId, page: 1, from },
    })
  }

  const selectContact = (event, item) => {
    const phoneNumber = item.suggestion.value
    dispatch(change(formName, 'from', phoneNumber))
    replace({
      pathname: '/dashboard/recordings',
      query: { ban, serviceId, page: 1, from: phoneNumber },
    })
  }

  const { data, loading } = useListRecordingsQuery({
    variables: { page, serviceId, ban, from },
  })

  if (!data || !data.recordings || loading) return <LoadingOverlay transparent />
  const { results, totalPages } = data.recordings

  return (
    <div>
      <div
        className={cn(
          sMarg.mb30,
          sLay['layout-gt-xs-column'],
          sLay['layout-gt-xs-row'],
          sLay['layout-align-start-center']
        )}
      >
        <div className={cn(sLay.flex, sText.descrip3, sMarg.mb15xs)}>
          Generated by 'Record Voicemail' and 'Forward Call' flow steps.
        </div>
        <Row className={cn(sLay['layout-gt-xs-column'], sLay['layout-gt-xs-row'], 'sm')}>
          <Col>
            <Field
              label="Contact"
              component={Autosuggest}
              autosuggestType="contact"
              onSuggestionSelected={selectContact}
              onClearRequested={() => selectContact(null, { suggestion: { value: '' } })}
              type="number"
              name="from"
              hideDot
              display="box"
            />
          </Col>
          <Col className={cn('sm', sMarg.mb15xs)}>
            <Field
              label="Billing Account"
              component={SelectPretty}
              options={banOptions}
              name="ban"
              display="box"
              onChangeAlt={selectBan}
            />
          </Col>
          <Col className={cn('sm')}>
            <Field
              label="Phone number"
              component={SelectPretty}
              options={numberOptions}
              name="serviceId"
              display="box"
              onChangeAlt={selectNumber}
            />
          </Col>
        </Row>
      </div>
      {results.length === 0 ? (
        <EmptyMessageCard
          loading={loading}
          title="You have no recordings yet."
          subtitle={`Create a flow and add a 'Record Voicemail' or 'Forward Call' node.`}
        />
      ) : (
        <div>
          <Table
            header={
              <TableHeader>
                <div style={{ maxWidth: '120px' }}>Date</div>
                <div style={{ maxWidth: '150px' }}>Phone number</div>
                <div className={sLay['hide-xs']} style={{ maxWidth: '140px' }}>
                  Type
                </div>
                <div className={sLay['hide-xs']} style={{ maxWidth: '115px' }}>
                  Transcription
                </div>
                <div>Audio</div>
                <div style={{ maxWidth: '76px' }} />
              </TableHeader>
            }
          >
            {results.map(result => {
              const date = moment(result.dateCreated).format('DD MMM YYYY')
              const time = moment(result.dateCreated).format('h:mm:ss a')
              const serviceNumber = numbersById[result.serviceId]

              return (
                <TableRow key={result.id}>
                  <div style={{ maxWidth: '120px' }}>
                    <div>{date}</div>
                    <div style={{ fontSize: '85%' }}>{time}</div>
                  </div>
                  <div style={{ maxWidth: '150px' }}>
                    {serviceNumber ? (
                      serviceNumber.twilioFlowServiceId ? (
                        numbersById[serviceNumber.twilioFlowServiceId].number
                      ) : (
                        serviceNumber.number
                      )
                    ) : (
                      <LoadingDots />
                    )}
                  </div>
                  <div className={sLay['hide-xs']} style={{ maxWidth: '140px' }}>
                    {result.source === 'DialVerb' ? 'Forward Call' : 'Voicemail'}
                  </div>
                  <div className={sLay['hide-xs']} style={{ maxWidth: '115px' }}>
                    {result.transcriptions.length > 0 ? 'Yes' : 'No'}
                  </div>
                  <div className={sLay['hide-xs']}>
                    <audio controls style={{ width: '100%' }}>
                      <source
                        src={`${config.api.portal}/api/v1/twilio/recordings/${result.sid}.mp3`}
                        type="audio/mpeg"
                      />
                    </audio>
                  </div>
                  <div style={{ maxWidth: '76px' }}>
                    <Button
                      color="tertiary"
                      size="xs"
                      ghost
                      to={`/dashboard/recordings/${result.id}`}
                    >
                      View
                    </Button>
                  </div>
                </TableRow>
              )
            })}
          </Table>
        </div>
      )}
      <Pagination
        page={page}
        numPages={totalPages}
        onClick={nextPage =>
          replace({
            pathname: location.pathname,
            query: {
              serviceId,
              ban,
              page: nextPage,
            },
          })
        }
      />
    </div>
  )
}



// WEBPACK FOOTER //
// src/features/Recordings/RecordingsList/RecordingsList.tsx